import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    ComponentDefinition,
    TagDefinition,
    TagDateValue,
    SeriesDefinition,
    ILineChartData,
    IValueViewModel,
    IPeriodConfiguration,
    DigitalSet,
    ConnectionPoint,
    RefreshClickData,
    StatsData,
    ActualDataType,
    DigitalSetSimpleDictionary,
    Dictionary,
    Group,
    TagStringValueDto
} from '../../../models';
import {
    IExportToCsvService,
    IrawValuesService,
    IDateRangeService,
    ItrendComparisonService,
    IwidgetIntervalHelperService,
    IAuthorizationService,
    ILoggerService,
    ITagPageService,
    IGroupTagsService,
    IStoreService,
    ISaveMessagesService,
    IStringValuesService
} from '../../../services';
import {
    momentFormat,
    Roles,
    isEditMode,
    compare,
    refreshClick,
    rawConnectionPointTypes,
    digitalRawConnectionPointTypes,
    stringRawConnectionPointTypes
} from '../../../common';
import { IRootScope } from 'IRootScope';
import { StoreElement, Store } from '../../../stateManagement';
import { ChartConfigurationOption } from '../../../wtFramework';

class TagStateController implements angular.IComponentController {
    averageSeries = false;
    canAcquire: boolean;
    canReadRaw: boolean;
    clicked: ILineChartData;
    connectionPoints: ConnectionPoint[];
    currentBeginDate: string;
    currentEndDate: string;
    currentInterval: any;
    data: (TagDateValue | any)[];
    digitalSets: DigitalSetSimpleDictionary;
    digitalSetsArray: DigitalSet[];
    formController: angular.IFormController;
    gauge: any;
    gradient = false;
    hasManualInputRole: boolean;
    hasRawValues: boolean;
    initialRange: { beginDate: string; endDate: string };
    isEditMode: boolean;
    isLoaded: boolean;
    isLoading: boolean;
    isSaving: boolean;
    loadingMessage: string;
    menu: { model: any; label: string; update: Function }[];
    options: ChartConfigurationOption;
    selectedPeriod;
    series: SeriesDefinition<TagDateValue>[];
    showRawValues: boolean;
    standardDeviation = false;
    statistics;
    statsData: StatsData;
    tagAttributesWidget;
    tagDefinition: TagDefinition;
    tagDefinitionGuid: string;
    tagValueViewModel: IValueViewModel;
    tags: TagDefinition[];
    valueViewModels: Dictionary<IValueViewModel>;

    private _comparisonSeries: SeriesDefinition<TagDateValue>[] = [];
    private _comparisonSeriesName: string;
    private _garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$q',
        'exportToCsvService',
        'rawValuesService',
        'dateRangeService',
        'tagPageService',
        'trendComparisonService',
        'groupTagsService',
        'widgetIntervalHelperService',
        'authorizationService',
        'loggerService',
        'storeService',
        'saveMessagesService',
        'stringValuesService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: ng.ui.IStateService,
        private _$q: angular.IQService,
        private _exportToCsvService: IExportToCsvService,
        private _rawValuesService: IrawValuesService,
        private _dateRangeService: IDateRangeService,
        private _tagPageService: ITagPageService,
        private _trendComparisonService: ItrendComparisonService,
        private _groupTagsService: IGroupTagsService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService,
        private _authorizationService: IAuthorizationService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService,
        private _saveMessagesService: ISaveMessagesService,
        private _stringValuesService: IStringValuesService
    ) {}

    $onInit() {
        this.data = [];
        this._garbages = [];
        this.hasManualInputRole = this._authorizationService.isInRoles(Roles.manualInputRoles);
        this.canAcquire = this._authorizationService.isInRoles(Roles.acquisitionRoles);
        this.isEditMode = this._$rootScope.isEditMode;
        this.selectedPeriod = '';
        this.tagDefinitionGuid = this._$state.params.tagDefinitionGuid;
        this.initializeOptions();
        if (this._$state.params.beginDate && this._$state.params.endDate) {
            this.initialRange = {
                beginDate: moment(this._$state.params.beginDate).format(momentFormat),
                endDate: moment(this._$state.params.endDate).format(momentFormat)
            };
        }
        this.initializeMenu();

        // Events handling
        this._garbages.push(
            this._$scope.$on(refreshClick, (event, data: RefreshClickData) => {
                this.refresh(data.beginDate, data.endDate, data.force);
            })
        );

        this._garbages.push(
            this._$scope.$on(isEditMode, (e, data) => {
                this.isEditMode = data;
            })
        );

        this._garbages.push(
            this._$scope.$on(compare, () => {
                try {
                    if (this._dateRangeService.compare()) {
                        this.createComparisonSeries();
                        this.readComparisonData();
                    } else {
                        _.remove(this.series, (s) => s.name.indexOf(this.compareName(this._comparisonSeriesName)) > -1);
                        _.remove(this._comparisonSeries, () => true);
                    }
                } catch (e) {
                    this._loggerService.logError(e);
                    this.isLoading = false;
                }
            })
        );

        this._garbages.push(
            this._$scope.$watchGroup([() => this.currentBeginDate, () => this.currentEndDate], () => {
                this.currentInterval = { startDate: this.currentBeginDate, endDate: this.currentEndDate };
            })
        );

        this._garbages.push(
            this._storeService.register(
                (store) => this.storeUpdate(store),
                [StoreElement.ConnectionPoints, StoreElement.DigitalSets, StoreElement.Groups, StoreElement.Tags, StoreElement.ValueViewModels]
            )
        );
    }

    $onDestroy() {
        if (this._garbages) {
            this._garbages.forEach((garbage) => garbage());
        }
    }

    initializeOptions() {
        const options = new ChartConfigurationOption();
        options.isTitleVisible = false;
        options.isLegendVisible = false;
        options.isHorizontalGridlinesVisible = true;
        options.panes = [
            {
                height: 1,
                isXaxisVisible: true,
                isYaxisVisible: true,
                isHorizontalGridLinesVisible: true,
                horizontalLines: [],
                isTitleLeftAligned: false,
                minHeight: null
            }
        ];
        this.options = options;
    }

    initializeMenu() {
        this.menu = [
            {
                model: this.averageSeries,
                label: 'Average',
                update: (value) => this.toggleStatsSeries('average', value)
            },
            {
                model: this.standardDeviation,
                label: 'Std dev',
                update: (value) => this.toggleStatsSeries('standardDeviation', value)
            },
            {
                model: this.gradient,
                label: 'Gradient',
                update: (value) => this.toggleStatsSeries('gradient', value)
            }
        ];
    }

    acquire() {
        let beginDate = this._dateRangeService.beginDate() as string;
        let endDate = this._dateRangeService.endDate() as string;
        const configuration: IPeriodConfiguration = {
            isEndDateIncluded: false,
            minPeriod: undefined,
            maxPeriod: undefined
        };
        const updatedInterval = this._widgetIntervalHelperService.getInterval(beginDate, endDate, configuration);
        beginDate = updatedInterval.beginMoment.toISOString();
        endDate = updatedInterval.endMoment.toISOString();
        this._groupTagsService
            .acquire([this.tagDefinition.guid], beginDate, endDate, true)
            .then((data) => {
                this._loggerService.logSuccess('Acquisition started.');
            })
            .catch((reason) => {
                this._loggerService.logError('Failed to start acquisition.', true, reason);
            });

        return false;
    }

    exportCsv() {
        this.isLoading = true;
        const dto: any[] = [];
        const configuration = {
            guid: this.tagDefinition.guid,
            type: 'tag',
            value: 'average'
        };
        dto.push(configuration);
        this._exportToCsvService.getCsv(dto, 'Trend').then(() => (this.isLoading = false));
    }

    exportRawCsv() {
        this.isLoading = true;
        const rawData: string[][] = [];
        rawData.push(['Timestamp', 'Local timestamp', this.tagDefinition.name]);
        rawData.push(['Comment', '', this.tagDefinition.importedComment]);
        rawData.push(['Unit', '', this.tagDefinition.importedUnit]);
        const fmt = 'YYYY-MM-DD HH:mm:ss';
        this.data
            .filter((d) => d.valueType === 'raw')
            .forEach((d: TagDateValue) => {
                const m = moment(d.timeStamp);
                const value = d.value === null || d.value === undefined ? '' : d.value.toString();
                rawData.push([m.clone().utc().format(fmt), m.format(fmt), value]);
            });
        this._exportToCsvService.getCsvFromData(rawData, `${this.tagDefinition.name}-Raw data.csv`);
        this.isLoading = false;
    }

    saveTag(): angular.IPromise<boolean> {
        this.isSaving = true;
        this.loadingMessage = 'Saving tag properties';
        const p = this._storeService.dispatchers().tags.update([this.tagDefinition]);
        return this._saveMessagesService
            .saveTag(p)
            .then(() => false)
            .catch(() => true)
            .finally(() => {
                this.isSaving = false;
                this.loadingMessage = null;
            });
    }

    refresh(beginDate?: string, endDate?: string, force: boolean = false) {
        this.isLoading = true;
        this.hasRawValues = false;
        this.showRawValues = false;
        const guid = this.tagDefinitionGuid;
        if (!beginDate) {
            beginDate = this._dateRangeService.beginDate() as string;
        }
        if (!endDate) {
            endDate = this._dateRangeService.endDate() as string;
        }
        if (this.options) {
            this.options.display.xMin = moment(beginDate).toDate();
            this.options.display.xMax = moment(endDate).toDate();
        }
        if (this.tagDefinition) {
            const isDigital = this.tagDefinition && this.tagDefinition.actualDataType === ActualDataType.Digital;
            const isString = this.tagDefinition && this.tagDefinition.actualDataType === ActualDataType.String;
            const valueType = isDigital || isString ? 'interpolated' : 'average';
            this.isLoading = true;
            if (isString) {
                this._stringValuesService
                    .getAll()
                    .then((stringValues) => {
                        this.readData(guid, beginDate, endDate, valueType, this.digitalSets, this.valueViewModels, force, stringValues);
                    })
                    .finally(() => (this.isLoading = false));
            } else {
                this.readData(guid, beginDate, endDate, valueType, this.digitalSets, this.valueViewModels, force).finally(() => (this.isLoading = false));
            }
        }
    }

    readRawData() {
        const found = _.find(this.series, (s) => s.identifier === `${this.tagDefinitionGuid}raw`);
        if (this.hasRawValues) {
            if (found) {
                found.isVisible = !found.isVisible;
                this.showRawValues = found.isVisible;
            }
        } else {
            this.isLoading = true;
            const isDigital = this.tagDefinition && this.tagDefinition.actualDataType === ActualDataType.Digital;
            const isString = this.tagDefinition && this.tagDefinition.actualDataType === ActualDataType.String;
            if (found && !found.isVisible) {
                found.isVisible = true;
            }
            const beginDate = this._dateRangeService.beginDate() as string;
            const endDate = this._dateRangeService.endDate() as string;
            this._rawValuesService
                .getRawData([this.tagDefinitionGuid], beginDate, endDate)
                .then((rawData) => {
                    let { values: data } = rawData;
                    if (isDigital) {
                        const digitalSet = this.digitalSets[this.tagDefinition.digitalSetGuid];
                        if (digitalSet) {
                            data = data.map((d) => {
                                d.comment = digitalSet[d.value];
                                return d;
                            });
                        }
                    }
                    if (isString) {
                        data = data.map((d) => {
                            const value = rawData.tagStringValues.find((tsv) => tsv.id === d.value);
                            d.comment = value ? value.stringValue : '-';
                            return d;
                        });
                    }
                    this.data = this.data ? this.data.concat(data) : data;
                    this.hasRawValues = true;
                    this.showRawValues = this.hasRawValues;
                })
                .finally(() => (this.isLoading = false));
        }
    }

    private readData(
        guid: string,
        beginDate: string,
        endDate: string,
        valueType: string,
        digitalSets: DigitalSetSimpleDictionary,
        viewModels: Dictionary<IValueViewModel>,
        force: boolean,
        stringValues?: TagStringValueDto[]
    ): angular.IPromise<void> {
        return this._tagPageService.readData(guid, beginDate, endDate, valueType, digitalSets, viewModels, force, stringValues).then((identifierData) => {
            this.data = identifierData.preparedData;
            this.statsData = identifierData.statsData;
        });
    }

    private storeUpdate(store: Store): void {
        const groups = store.groups.dictionary;
        this.digitalSets = store.digitalSetDictionary.dictionary;
        this.digitalSetsArray = _.values<DigitalSet>(store.digitalSets.dictionary);
        this.tags = _.values(store.tags.dictionary);
        this.valueViewModels = store.valueViewModels.dictionary;
        this.connectionPoints = _.values(store.connectionPoints.dictionary);
        this.isLoaded = true;
        this.isLoading = false;

        if (this.tagDefinitionGuid) {
            const foundVvm = store.valueViewModels.dictionary[this.tagDefinitionGuid];
            const foundTag = store.tags.dictionary[this.tagDefinitionGuid];
            this.tagValueViewModel = foundVvm;
            this.tagDefinition = foundTag;
            if (foundTag) {
                this.refreshWidgets(foundTag);
            }

            const cp = store.connectionPoints.dictionary[foundTag.acquisitionPointGuid];
            if (cp) {
                const collection =
                    foundTag.actualDataType === ActualDataType.Float
                        ? rawConnectionPointTypes
                        : foundTag.actualDataType === ActualDataType.Digital
                        ? digitalRawConnectionPointTypes
                        : stringRawConnectionPointTypes;
                this.canReadRaw = collection.indexOf(cp.type) >= 0;
            } else {
                this.canReadRaw = false;
            }
            this.loadSerieWithTag(foundTag, groups);
            this.refresh(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string);
        }
    }

    private refreshWidgets(tagDefinition: TagDefinition) {
        if (tagDefinition) {
            const widgets = this._tagPageService.createWidgets(tagDefinition);
            this.gauge = widgets.gauge;
        }
    }

    private loadSerieWithTag(tagDefinition: TagDefinition, groups: Dictionary<Group>) {
        if (tagDefinition) {
            this.series = this._tagPageService.createSeries(tagDefinition);
            this._$state.current.data.breadcrumbs.level4 = tagDefinition.name;

            this._tagPageService
                .loadMinMax(tagDefinition, this.valueViewModels[tagDefinition.guid], groups)
                .then((data) => (this.options.panes[0].horizontalLines = data));
        }
    }

    private toggleStatsSeries(stat: string, value: boolean) {
        const found = _.find(this.series, (s) => s.identifier === `stats${stat}`);
        if (found) {
            found.isVisible = value;
        }
    }

    private compareName(serieName): string {
        return ` (${serieName})`;
    }

    private createComparisonSeries() {
        this.isLoading = true;
        if (this._comparisonSeries.length === 0) {
            const duration = this._dateRangeService.getComparisonDuration();
            const filteredSeries = this.series.filter((s) => s.name === this.tagDefinition.name);
            const cs = this._trendComparisonService.createComparisonSeries(filteredSeries, duration);
            this._comparisonSeries = cs.comparisonSeries;
            this._comparisonSeriesName = cs.newName;
            this.series = this.series.concat(this._comparisonSeries);
        } else {
            this.renameComparisonSeries();
        }
        this.isLoading = false;
    }

    private readComparisonData() {
        if (this._comparisonSeries.length > 0 && this._dateRangeService.initialized()) {
            this.renameComparisonSeries();
            this.isLoading = true;
            _.remove(this.data, (d) => d.valueType && d.valueType.indexOf('comparison') > -1);
            const cr = this._dateRangeService.getComparisonPeriod();
            const duration = this._dateRangeService.getComparisonDuration();
            this._trendComparisonService
                .readComparisonData(this.tagDefinition.guid, cr, duration)
                .then((data) => {
                    if (data.length) {
                        const maxEndDate = d3.max(data, (d: any) => d.timeStamp);
                        const endDate = this._dateRangeService.endDate() as string;
                        if (maxEndDate < endDate) {
                            this.options.display.xMax = moment(endDate).toDate();
                        } else {
                            this.options.display.xMax = moment(maxEndDate).toDate();
                        }
                        this.data = this.data.concat(data);
                    }
                })
                .finally(() => (this.isLoading = false));
        }
    }

    private renameComparisonSeries() {
        const comparisonDuration = this._dateRangeService.getComparisonDuration();
        const res = this._trendComparisonService.renameComparisonSeries(this._comparisonSeries, this._comparisonSeriesName, comparisonDuration);
        this._comparisonSeries = res.comparisonSeries;
        this._comparisonSeriesName = res.newName;
    }
}

export const wtTagState: ComponentDefinition = {
    componentId: 'wtTagState',
    controller: TagStateController,
    template: require('./tagState.component.html'),
    controllerAs: 'vm',
    bindings: {}
};

export const wtTagGeneralState: ComponentDefinition = {
    componentId: 'wtTagGeneralState',
    controller: TagStateController,
    template: require('./tagState.general.component.html'),
    controllerAs: 'vm',
    bindings: {}
};
